/** One rendered line's height, in pixels; the viewer's CSS uses the same value. */
export const LINE_HEIGHT_PX = 20;
/** Files up to this many lines are rendered whole; longer ones only around what is on screen. */
export const WINDOW_THRESHOLD = 400;
/** Lines rendered above and below what is on screen, so a fast scroll does not show blank space. */
export const OVERSCAN = 40;

export interface LineRange {
  /** Index of the first rendered line, from 0. */
  from: number;
  /** One past the last rendered line. */
  to: number;
}

/**
 * Which lines of a long file to render for a scroll position (MASTER-SPEC §14.10: the viewer virtualizes long
 * files). Short files are rendered whole, which keeps them fully readable by a screen reader and searchable with the
 * browser's find.
 */
export function visibleRange(
  total: number,
  scrollTop: number,
  viewportHeight: number,
  overscan = OVERSCAN,
): LineRange {
  if (total <= WINDOW_THRESHOLD) {
    return { from: 0, to: total };
  }
  const first = Math.floor(Math.max(scrollTop, 0) / LINE_HEIGHT_PX);
  const shown = Math.ceil(Math.max(viewportHeight, 0) / LINE_HEIGHT_PX);
  return {
    from: Math.max(0, first - overscan),
    to: Math.min(total, first + shown + overscan),
  };
}

/** The scroll position that puts `index` a few lines below the top. */
export function scrollTopFor(index: number): number {
  return Math.max(0, (index - 3) * LINE_HEIGHT_PX);
}

/** A file's content as lines. A trailing newline does not make an empty last line. */
export function splitLines(content: string): string[] {
  const lines = content.split(/\r?\n/);
  if (lines.length > 1 && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines;
}

/** The 2 000-line page of a file that holds `line` (the API returns at most 2 000 lines at a time). */
export const PAGE_LINES = 2_000;
export function pageStart(line: number): number {
  return Math.floor((Math.max(line, 1) - 1) / PAGE_LINES) * PAGE_LINES + 1;
}
